import * as types from './mutation-types';
import { getWebTranslatedEntityUrl } from 'utils/adminToWebRouteMapping';

export default {
  /**
   * KNOWLEDGE
   */
  async getKnowledges({ commit, state, dispatch }) {
    try {
      commit(types.SET_LOADING, true);
      const knowledges = await this.$services.hierarchies.getKnowledges();
      commit(types.SET_KNOWLEDGES, knowledges);
      if (
        !(
          state.focusKnowledge &&
          knowledges.find((k) => k.id === state.focusKnowledge.id)
        )
      ) {
        let newFocusKnowledge = knowledges[0];
        commit(types.SET_FOCUS_KNOWLEDGE, newFocusKnowledge);
        commit(
          types.SET_NAVIGATION_LANGUAGE,
          newFocusKnowledge.defaultLanguage,
        );
        dispatch('getKnowledgeRoots');
        return newFocusKnowledge.value;
      }
    } catch (err) {
      return err;
    } finally {
      commit(types.SET_LOADING, false);
    }
  },

  getFocusKnowledgeById({ commit, state, dispatch }, knowledgeId) {
    const knowledges = state.knowledges;
    const focusKnowledge = knowledges.find((k) => k.id === knowledgeId);
    commit(types.SET_FOCUS_KNOWLEDGE, focusKnowledge);
    dispatch('getKnowledgeRoots');
  },

  async initKnowledge({ state, commit, dispatch }) {
    if (state.focusKnowledge) commit(types.SET_FOCUS_KNOWLEDGE, null);

    await dispatch('getUserInfos');
    return dispatch('getKnowledges');
  },

  /**
   * KNOWLEDGE ROOTS
   */
  async getKnowledgeRoots({ commit, getters, dispatch }) {
    let { focusKnowledgeValue, refreshUserInfos, navigationLanguage } = getters;
    try {
      if (!focusKnowledgeValue) {
        focusKnowledgeValue = await dispatch('getKnowledges');
      }
      const focusKnowledgeRoots = await this.$services.cases.getRoots(
        focusKnowledgeValue,
        navigationLanguage,
      );

      commit(types.SET_FOCUS_KNOWLEDGE_ROOTS, focusKnowledgeRoots);

      if (refreshUserInfos(Date.now())) dispatch('getUserInfos');
    } catch (err) {
      return err;
    }
  },

  clearFocusItems({ commit }) {
    commit(types.UPDATE_PATH, []);
    commit(types.UPDATE_DIAG_PATH, []);
    commit(types.SET_FOCUS_NAV_ITEM, null);
    commit(types.SET_FOCUS_DIAG_ITEM, null);
  },

  /**
   * ITEMS
   */
  async getCaseById({ commit, getters, dispatch }, caseId) {
    commit(types.SET_LOADING, true);
    const skipOrdered = false;
    const skipPath = false;
    const publishedContentsOnly = true;

    try {
      const focusNavItem = await this.$services.cases.getById(
        caseId,
        skipOrdered,
        skipPath,
        getters.navigationLanguage,
        publishedContentsOnly,
      );
      commit(types.SET_FOCUS_NAV_ITEM, focusNavItem);
      const newPath = focusNavItem.path.map((item) => ({
        ...item,
        type: 'Case',
      }));
      dispatch('resolveFocusKnowledge', focusNavItem);
      commit(types.UPDATE_PATH, newPath);
    } finally {
      commit(types.SET_LOADING, false);
    }
  },

  async fetchCaseChildrenById({ getters }, caseId) {
    const { navigationLanguage } = getters;
    const skipOrdered = false;
    const skipPath = false;
    const publishedContentsOnly = true;

    try {
      const folder = await this.$services.cases.getById(
        caseId,
        skipOrdered,
        skipPath,
        navigationLanguage,
        publishedContentsOnly,
      );
      if (!folder) return null;

      const { orderedChildrenAndContents } = folder;

      return orderedChildrenAndContents;
    } catch (err) {
      return null;
    }
  },

  async getContentById(
    { state, getters, commit, dispatch },
    { contentId, stepId },
  ) {
    commit(types.SET_LOADING, true);
    try {
      const { navigationLanguage } = getters;
      let { path } = state;

      if (contentId && stepId) {
        if (!path.length || path[path.length - 1].id !== contentId) {
          const { focusNavItem } = await dispatch(
            'getPathFromContentId',
            contentId,
          );
          commit(types.SET_FOCUS_NAV_ITEM, focusNavItem);
        }

        const focusDiagItem =
          await this.$services.parametricContents.getWithAncestors(
            stepId,
            navigationLanguage,
            contentId,
          );
        if (focusDiagItem.redirection) {
          const isStep = ['Step', 'keyStep'].includes(
            focusDiagItem.redirection.entityType,
          );
          const stepId = focusDiagItem.redirection.entityId;
          const contentId = isStep
            ? await dispatch('getParentId', stepId)
            : focusDiagItem.redirection.entityId;
          await dispatch('goToContent', {
            contentId,
            ...(isStep ? { stepId: focusDiagItem.redirection.entityId } : {}),
          });
          return;
        }
        focusDiagItem.children = focusDiagItem.children.filter(
          (c) => !c.redirection || !c.redirection.isDeleted,
        );

        const { ancestorsContent } = focusDiagItem;
        const newDiagPath = [
          ...ancestorsContent.map((c) => ({
            id: c.id,
            label: c.label,
            type: 'Step',
          })),
          {
            id: focusDiagItem.id,
            label: focusDiagItem.label,
            type: focusDiagItem.type,
          },
        ];
        newDiagPath[0].type = 'Diagnostic';

        dispatch('resolveFocusKnowledge', focusDiagItem);
        commit(types.UPDATE_DIAG_PATH, newDiagPath);
        commit(types.SET_FOCUS_DIAG_ITEM, focusDiagItem);
      } else if (contentId) {
        if (!path.length || path[path.length - 1].id !== contentId) {
          const { path: resolvedPath } = await dispatch(
            'getPathFromContentId',
            contentId,
          );
          path = resolvedPath;
        }
        const focusNavItem = await this.$services.parametricContents.get(
          contentId,
          navigationLanguage,
        );

        if (['Step', 'keyStep'].includes(focusNavItem.type)) {
          return dispatch('goToWebTranslatedEntity', {
            entityType: focusNavItem.type,
            entityId: contentId,
            lang: navigationLanguage,
          });
        }

        dispatch(
          'setUserRecentlyConsultedContent',
          { content: focusNavItem, lang: navigationLanguage },
          { root: true },
        );
        const pathNavItem = {
          id: focusNavItem.id,
          label: focusNavItem.label,
          type: focusNavItem.type,
        };

        focusNavItem.children = focusNavItem.children.filter(
          (c) => !c.redirection || !c.redirection.isDeleted,
        );
        let newPath =
          path[path.length - 1].id !== focusNavItem.id
            ? path.concat(pathNavItem)
            : path;

        dispatch('resolveFocusKnowledge', focusNavItem);
        commit(types.UPDATE_PATH, newPath);
        commit(types.UPDATE_DIAG_PATH, [pathNavItem]);
        commit(types.SET_FOCUS_DIAG_ITEM, focusNavItem);
        commit(types.SET_FOCUS_NAV_ITEM, focusNavItem);
      }
    } finally {
      commit(types.SET_LOADING, false);
    }
  },

  async goToContent({ state, dispatch }, { contentId, stepId, hash, lang }) {
    lang = await dispatch('resolveLanguage', lang);

    const isStep = stepId && !!stepId.length;
    const rootContent = await this.$services.parametricContents.get(
      contentId,
      lang,
    );
    if (!rootContent) return false;

    const content = isStep
      ? await this.$services.parametricContents.get(stepId, lang)
      : rootContent;
    if (!content) return false;

    let { knowledges } = state;
    if (!knowledges.length) {
      await dispatch('getKnowledges');
      knowledges = state.knowledges;
    }

    const targetKnowledge = knowledges.find(
      (k) => k.value === content.knowledgeId,
    );

    if (!targetKnowledge) return false;

    const path = `/web/${lang}/knowledge/${targetKnowledge.id}/content/${contentId}`;
    const fullPath = isStep ? `${path}/step/${stepId}` : path;
    this.$router.replace({
      path: fullPath,
      hash,
    });
    return true;
  },

  async goToCase({ state, dispatch }, { caseId, lang }) {
    lang = await dispatch('resolveLanguage', lang);

    const collection = await this.$services.cases.getById(
      caseId,
      true,
      true,
      lang,
    );
    if (!collection) return false;

    let { knowledges } = state;
    if (!knowledges.length) {
      await dispatch('getKnowledges');
      knowledges = state.knowledges;
    }

    const targetKnowledge = knowledges.find(
      (k) => k.value === collection.knowledge,
    );

    if (!targetKnowledge) return false;

    const path = `/web/${lang}/knowledge/${targetKnowledge.id}/case/${caseId}`;
    this.$router.push({
      path,
    });

    return true;
  },

  /**
   * NAVIGATION
   */
  async getParentId({ dispatch }, stepId) {
    const content = await this.$services.parametricContents.getWithAncestors(
      stepId,
    );

    if (!content)
      return dispatch('knowledgeModule/flagRedirectionStepsAsDeleted', stepId, {
        root: true,
      });
    return content.ancestorsContent[0].id;
  },

  /**
   *
   * @param {*} param0
   * @param {*} args { entityId, entityType, parentId, lang, hash, query, open}
   * @returns
   */
  async goToWebTranslatedEntity({ dispatch, getters }, args) {
    const { open, lang, entityId, entityType } = args;
    const resolvedLang = await dispatch('resolveLanguage', lang);
    const { focusKnowledgeId } = getters;

    let parentId;

    if (['Step', 'keyStep'].includes(entityType)) {
      parentId = args.parentId || (await dispatch('getParentId', entityId));
    }

    const { name, params, query, hash } = await getWebTranslatedEntityUrl({
      ...args,
      parentId,
      lang: resolvedLang,
      knowledgeId: focusKnowledgeId,
    });
    const { href } = this.$router.resolve({
      name,
      params,
      query,
      hash,
    });
    const url = `${href}${hash}`;

    return open ? window.open(url) : this.$router.push(url);
  },

  /**
   * This action is called in getContentById and getCaseById
   * to make sure the knowledgeId passed in url params
   * is the same as the fetched item
   *
   * @param {*} param
   * @param {*} entity
   * @returns
   */
  async resolveFocusKnowledge({ getters }, entity) {
    if (!entity) return;

    const { focusKnowledge, knowledges } = getters;

    const { id, value } = focusKnowledge;
    const { knowledgeId } = entity; // Be careful as knowledgeId can be a knowledgeValue for contents here....

    if (id === knowledgeId || value === knowledgeId) return; // Already focused on the right knowledge

    const { name, query, params } = this.$router.currentRoute;

    const resolvedFocusKnowledge = knowledges.find(
      (k) => k.id === knowledgeId || k.value === knowledgeId,
    );

    if (!resolvedFocusKnowledge) return;

    return this.$router.replace({
      name,
      query,
      params: { ...params, knowledgeId: resolvedFocusKnowledge.id },
    });
  },

  setNavigationLanguage({ commit, getters }, language) {
    if (getters.navigationLanguage === language) return;

    commit(types.UPDATE_PATH, []);
    commit(types.SET_NAVIGATION_LANGUAGE, language);
  },

  resolveLanguage({ getters }, lang) {
    if (lang) return lang;
    const currentLanguage = this.$router.currentRoute.params.lang;
    return currentLanguage
      ? currentLanguage
      : getters.navigationLanguage || 'default';
  },

  updatePath({ commit }, newPath) {
    commit(types.UPDATE_PATH, newPath);
    commit(types.SET_FOCUS_NAV_ITEM, newPath[newPath.length - 1]);
  },

  updateDiagPath({ commit }, newPath) {
    commit(types.UPDATE_DIAG_PATH, newPath);
    commit(types.SET_FOCUS_NAV_ITEM, newPath[newPath.length - 1]);
  },

  async getPathFromContentId({ commit, getters }, contentId) {
    const { navigationLanguage } = getters;
    const focusNavItem = await this.$services.parametricContents.get(
      contentId,
      navigationLanguage,
    );
    let path;
    const pathNavItem = {
      id: focusNavItem.id,
      label: focusNavItem.label,
      type: focusNavItem.type,
    };

    if (focusNavItem.caseParameters.length) {
      const cases = focusNavItem.caseParameters[0].split('/');
      const caseId = cases[cases.length - 1];
      const skipOrdered = false;
      const skipPath = false;

      const caseItem = await this.$services.cases.getById(
        caseId,
        skipOrdered,
        skipPath,
        navigationLanguage,
      );

      const casesPath = caseItem.path.map((item) => ({
        ...item,
        type: 'Case',
      }));

      path = [...casesPath, pathNavItem];
    } else {
      path = [pathNavItem];
    }

    commit(types.UPDATE_PATH, path);
    return { path, focusNavItem };
  },

  updatePreviousItems({ commit }, previousItems) {
    commit(types.UPDATE_PREVIOUS_ITEMS, previousItems);
  },

  routeToPath({ getters, dispatch }, { item, source, open = false } = {}) {
    const { focusKnowledge } = getters;

    const routeKnowledgeId = this.$router.history.current.params.knowledgeId;
    const knowledgeId = focusKnowledge ? focusKnowledge.id : routeKnowledgeId;

    let path = `/web/knowledge/${knowledgeId}`;
    if (!item) {
      if (getters.searchQuery || getters.searchFilters.length) {
        dispatch('storeSearchAbortEvent');
      }
      dispatch('initSearch');
      return this.$router.push({ path });
    }

    const itemType = !item.type || item.type === 'Case' ? 'case' : 'content';
    path = path + `/${itemType}/`;
    if (item.type === 'Step' || item.type === 'keyStep') {
      const diagId = item.ancestors[0].split('/')[0];
      const stepId = item.id;
      path = path + `${diagId}/step/${stepId}`;
    } else {
      path = path + item.id;
    }

    if (open) {
      return window.open(`${path}?source=${source}`);
    }

    return this.$router.push({
      path: path,
      query: { source },
    });
  },

  /**
   * FEEDBACK
   */
  sendFeedback(_, feedback) {
    return this.$services.users.sendFeedback(feedback);
  },

  /**
   * USER
   */
  async getUserInfos({ commit }) {
    const { data } = await this.$services.users.getUserInfos();
    if (data.user)
      commit(types.SET_USER_INFOS, {
        ...data.user,
        lastUserInfosRefresh: Date.now(),
      });
  },

  async updateUserFavorites({ dispatch, rootGetters }, entity) {
    const userFavorites = rootGetters['userFavoritesModule/favorites'];
    const isRemove = userFavorites.find((f) => {
      return f.entity.id === entity.entityId;
    });
    const eventName = isRemove ? 'removeFavorite' : 'addFavorite';
    try {
      if (isRemove)
        dispatch('userFavoritesModule/removeUserFavorite', isRemove.id, {
          root: true,
        });
      else
        dispatch('userFavoritesModule/addUserFavorite', entity.entityId, {
          root: true,
        });

      this.$services.events.content[eventName](
        {
          id: entity.entityId,
          type: entity.entityType,
          totalFavorites: isRemove
            ? userFavorites.length - 1
            : userFavorites.length + 1,
        },
        this.$router.history.current.path,
        'MAYDAY_WEB',
      );
    } catch (e) {
      return e;
    }
  },

  /**
   * SEARCH
   */
  async maydayWebSearchAction(
    { state, commit, getters, rootGetters, dispatch },
    { query, entityType, categories, caseId },
  ) {
    commit(types.SET_LOADING, true);
    try {
      const {
        focusKnowledgeValue: knowledge,
        navigationLanguage,
        searchFocusLevel,
      } = getters;

      const res = await dispatch(
        'searchAction',
        {
          searchType: 'maydayWebSearch',
          searchArgs: {
            query,
            entityType,
            categories,
            size: state.size,
            hasEngineRestrictions: rootGetters.hasAgentRestrictions,
            knowledge,
            lang: navigationLanguage,
            ...(searchFocusLevel === 'case' ? { caseId } : {}),
          },
        },
        { root: true },
      );

      dispatch('updateSearchResults', {
        results: res.documents,
        totalCount: res.counts.totalCount,
      });

      const currentFilter = {
        lang: navigationLanguage,
        filterType: ['attached-contents', 'cases'],
        entityType: entityType,
        search: query,
        knowledge: knowledge,
        product: null,
        cases: null,
        status: [{ key: 'published', value: true }],
        accessRestrictions: null,
        labels: categories,
        isContribution: null,
        searchFrom: 0,
        size: state.size,
      };

      dispatch('storeSearchEvent', {
        eventMethod: 'search',
        event: {
          search: currentFilter,
          displayedHits: res.documents.length,
          totalHits: res.counts.totalCount,
        },
      });
    } finally {
      commit(types.SET_LOADING, false);
    }
  },

  searchDisplayMore({ getters, commit }) {
    const { searchTotalCount, searchSize } = getters;
    const size =
      searchTotalCount < searchSize + 10 ? searchTotalCount : searchSize + 10;

    commit(types.UPDATE_SEARCH_SIZE, size);
  },

  updateSearchQuery({ commit }, query) {
    commit(types.UPDATE_SEARCH_QUERY, query);
  },

  updateSearchFilters({ commit }, filters) {
    commit(types.UPDATE_SEARCH_FILTERS, filters);
  },
  updateSearchFocusLevel({ commit }, focusLevel) {
    commit(types.UPDATE_SEARCH_FOCUS_LEVEL, focusLevel);
  },

  updateSearchResults({ commit }, payload) {
    commit(types.UPDATE_SEARCH_RESULTS, payload);
  },

  initSearch({ commit }) {
    commit(types.UPDATE_SEARCH_QUERY, '');
    commit(types.UPDATE_SEARCH_SIZE, 10);
    commit(types.UPDATE_SEARCH_FOCUS_LEVEL, '');
    commit(types.UPDATE_SEARCH_FILTERS, []);
    commit(types.UPDATE_SEARCH_RESULTS, { results: [], totalCount: 0 });
  },

  updateRecentSearch({ commit, getters }, { query, filters } = {}) {
    if (query || filters) {
      commit(types.UPDATE_RECENT_SEARCHES, {
        searchQuery: query,
        searchFilters: filters,
      });
    } else {
      const { searchQuery, searchFilters } = getters;
      if (
        !searchQuery &&
        !searchQuery.length &&
        !searchFilters &&
        !searchFilters.length
      ) {
        return;
      }
      commit(types.UPDATE_RECENT_SEARCHES, { searchQuery, searchFilters });
    }
  },

  async getTopContents({ commit, getters }, knowledgeId) {
    commit;

    const { focusKnowledgeId } = getters;

    const result = await this.$services.parametricContents.getTopContents(
      knowledgeId || focusKnowledgeId,
    );

    commit(types.UPDATE_FREQUENT_SEARCHES, result);

    return result;
  },

  /**
   * EVENT
   */
  storeSearchEvent({ getters }, { eventMethod, event }) {
    try {
      const { focusKnowledgeId } = getters;
      event.knowledgeId = focusKnowledgeId;
      this.$services.events.content[eventMethod](
        event,
        window.location.href,
        'MAYDAY_WEB',
      );
    } catch (e) {
      console.log(e);
    }
  },

  storeSearchAbortEvent({ getters, dispatch }) {
    dispatch('storeSearchEvent', {
      eventMethod: 'searchAbort',
      event: {
        search: getters.currentFilter,
        displayedHits: getters.searchResults.length,
        totalHits: getters.searchTotalCount,
      },
    });
  },

  storeSearchSuccessEvent({ getters, dispatch }, { item, itemIndex }) {
    const {
      navigationLanguage,
      currentFilter,
      searchResults,
      searchTotalCount,
    } = getters;

    dispatch('updateRecentSearch');
    const event = {
      eventMethod: 'searchSuccess',
      event: {
        content: item,
        search: { ...currentFilter, lang: navigationLanguage },
        itemIndex,
        displayedHits: searchResults.length,
        totalHits: searchTotalCount,
      },
    };
    dispatch('storeSearchEvent', event);
  },
};
